import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";

import { getPricingTimeListUseCase } from "../domain/usecases/getPricingTimeListUseCase";
import { deletePricingTimeUseCase } from "../domain/usecases/deletePricingTimeUseCase";
import Topbar from "../../../components/topbar/TopBar";

const DAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miercoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sabado" },
  { value: 7, label: "Domingo" },
];

function parseTimeToMinutes(value) {
  if (!value) {
    return Number.MAX_SAFE_INTEGER;
  }

  const [hour = "0", minute = "0"] = value.split(":");
  return Number(hour) * 60 + Number(minute);
}

function formatTime(value) {
  if (!value) {
    return "--:--";
  }

  return value.slice(0, 5);
}

function getDaySchedules(pricingTimes, dayOfWeek) {
  return pricingTimes
    .filter((item) => Number(item.dayOfWeek) === dayOfWeek)
    .sort(
      (left, right) =>
        parseTimeToMinutes(left.startTime) -
        parseTimeToMinutes(right.startTime),
    );
}

export default function PricingTimeListPage() {
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState(1);
  const [pricingTimes, setPricingTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadPricingTimes = async () => {
    try {
      setLoading(true);
      setError("");
      setPricingTimes(await getPricingTimeListUseCase());
    } catch (requestError) {
      setError(
        requestError.message || "No se pudo cargar la lista de horarios.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricingTimes();
  }, []);

  const selectedDaySchedules = useMemo(
    () => getDaySchedules(pricingTimes, selectedDay),
    [pricingTimes, selectedDay],
  );

  const handleOpenDeleteDialog = (pricingTime) => {
    setDeleteTarget(pricingTime);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) {
      return;
    }

    setDeleteTarget(null);
  };

  const handleDeletePricingTime = async () => {
    if (!deleteTarget?.id) {
      return;
    }

    try {
      setDeleting(true);
      await deletePricingTimeUseCase(deleteTarget.id);
      setPricingTimes((prev) =>
        prev.filter((pricingTime) => pricingTime.id !== deleteTarget.id),
      );
      setMessage({
        open: true,
        type: "success",
        text: "Horario eliminado correctamente.",
      });
      setDeleteTarget(null);
    } catch (requestError) {
      setMessage({
        open: true,
        type: "error",
        text: requestError.message || "No se pudo eliminar el horario.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": {
              color: "#000",
            },
            p: 1,
          }}
        >
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                  color: "#fff",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      HORARIOS
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/admin/precio-horario")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "& .MuiButton-startIcon": {
                        color: "#fff",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#fff",
                      },
                      "& .MuiButton-label": {
                        fontWeight: 700,
                      },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Agregar Horario
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ px: { xs: 1, md: 2 }, pt: 1, bgcolor: "#fff" }}>
                <Tabs
                  value={selectedDay}
                  onChange={(event, value) => setSelectedDay(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    "& .MuiTab-root": {
                      minHeight: 54,
                      textTransform: "none",
                      fontSize: "0.98rem",
                      fontWeight: 700,
                      color: "#000",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      color: "#000",
                      fontWeight: 800,
                      fontSize: "1.105rem",
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#000",
                    },
                  }}
                >
                  {DAYS.map((day) => (
                    <Tab key={day.value} value={day.value} label={day.label} />
                  ))}
                </Tabs>
              </Box>
            </Paper>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(16, 24, 40, 0.08)",
                overflow: "hidden",
                bgcolor: "#fff",
                padding: 2,
              }}
            >
              <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      {DAYS.find((day) => day.value === selectedDay)?.label}
                    </Typography>
                  </Box>

                  <Chip
                    size="medium"
                    label={`${selectedDaySchedules.length} registros`}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#edf7f3",
                      color: "#165c53",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ p: { xs: 1, md: 2 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando horarios...
                    </Typography>
                  </Stack>
                ) : null}

                {!loading && selectedDaySchedules.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 4,
                      textAlign: "center",
                      bgcolor: "#fbfcfc",
                    }}
                  >
                    <Typography fontWeight={700}>
                      Sin horarios registrados
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      No hay configuraciones disponibles para este dia.
                    </Typography>
                  </Paper>
                ) : null}

                {!loading && selectedDaySchedules.length > 0 ? (
                  <Grid container spacing={2} sx={{ rowGap: 3 }}>
                    {selectedDaySchedules.map((pricingTime) => (
                      <Grid
                        item
                        xs={12}
                        md={4}
                        xl={4}
                        key={pricingTime.id}
                        marginBottom={2}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            height: "100%",
                            minHeight: 100,
                            borderRadius: 3,
                            border: "1px solid rgba(20, 83, 45, 0.12)",
                            background:
                              "linear-gradient(180deg, #ffffff 0%, #f8fbfa 100%)",
                            p: 2.25,
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1.5}
                            >
                              <Box>
                                <Typography variant="h6" fontWeight={800}>
                                  {pricingTime.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 0.5, color: "#000" }}
                                >
                                  {pricingTime.active ? "Activo" : "Inactivo"}
                                </Typography>
                              </Box>
                            </Stack>

                            <Divider />

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <AccessTimeIcon sx={{ color: "#165c53" }} />
                              <Typography
                                fontWeight={700}
                                sx={{ color: "#000" }}
                              >
                                {formatTime(pricingTime.startTime)} -{" "}
                                {formatTime(pricingTime.endTime)}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <TrendingUpIcon sx={{ color: "#c56a19" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#000" }}
                              >
                                Multiplicador:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {pricingTime.multiplier}
                                </Box>
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              sx={{ pt: 0.75 }}
                            >
                              <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  handleOpenDeleteDialog(pricingTime)
                                }
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 700,
                                  textTransform: "none",
                                }}
                              >
                                Eliminar
                              </Button>
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() =>
                                  navigate(
                                    `/admin/precio-horario/actualizar/${pricingTime.id}`,
                                  )
                                }
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 700,
                                  textTransform: "none",
                                  bgcolor: "#165c53",
                                  color: "#fff",
                                  "&:hover": {
                                    bgcolor: "#12483f",
                                  },
                                }}
                              >
                                Modificar
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : null}
              </Box>
            </Paper>
          </Stack>

          <Dialog
            open={Boolean(deleteTarget)}
            onClose={handleCloseDeleteDialog}
            PaperProps={{
              sx: {
                borderRadius: 3,
              },
            }}
          >
            <DialogTitle sx={{ color: "#000" }}>
              Desea Eliminar este Horario?
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "#000" }}>
                {deleteTarget?.name || "Este horario"} sera eliminado de forma
                permanente.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button
                onClick={handleCloseDeleteDialog}
                disabled={deleting}
                sx={{
                  textTransform: "none",
                  color: "#000",
                  fontWeight: 700,
                }}
              >
                No
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeletePricingTime}
                disabled={deleting}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                {deleting ? "Eliminando..." : "Si, eliminar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
